<script setup lang="ts">
import { computed } from 'vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import { IconDocumentLock, IconFolder } from '@/components/design/icons';
import { gaiaAccent, gaiaNeutral } from '@/components/design/themes/tokens';

/**
 * The yes/no before a drop-to-upload goes ahead: always onto a contract or a
 * folder, and onto the current folder once the drop is large. Just the question and the target —
 * no title bar.
 */
const props = defineProps<{
  show: boolean;
  /** Name of the folder or contract the drop lands in. */
  targetName: string;
  targetKind: 'directory' | 'contract';
  fileCount: number;
}>();

const emit = defineEmits<{
  'update:show': [value: boolean];
  confirm: [];
}>();

const question = computed(() => {
  const files = props.fileCount === 1 ? '1 file' : `${props.fileCount} files`;
  const target = props.targetKind === 'contract' ? 'contract' : 'folder';
  return `Do you want to add ${files} to the ${target}:`;
});

function confirm(): void {
  emit('confirm');
  emit('update:show', false);
}
</script>

<template>
  <gaia-modal
    :show="props.show"
    width="420px"
    :closable="false"
    @update:show="(value: boolean) => emit('update:show', value)"
  >
    <n-flex vertical :size="12" :wrap="false">
      <gaia-text size="body" tone="ink">{{ question }}</gaia-text>
      <!-- The target, set off on a tint so it reads as the thing being asked about. -->
      <n-flex
        align="center"
        :size="8"
        :wrap="false"
        :style="{ padding: '9px 12px', borderRadius: '9px', background: gaiaNeutral.surfaceTint }"
      >
        <n-icon
          :component="props.targetKind === 'contract' ? IconDocumentLock : IconFolder"
          :size="16"
          :color="gaiaAccent.base"
          :style="{ flex: 'none' }"
        />
        <gaia-text truncate size="body" weight="semibold" tone="ink">
          {{ props.targetName }}
        </gaia-text>
      </n-flex>
    </n-flex>

    <template #action>
      <n-flex justify="end" :size="8" :wrap="false">
        <gaia-button @click="emit('update:show', false)">No</gaia-button>
        <gaia-button type="primary" @click="confirm">Yes</gaia-button>
      </n-flex>
    </template>
  </gaia-modal>
</template>
